import type { PlayerLine, RosterSide } from '../api';

export default function LineupsPanel({ rosters }: { rosters: RosterSide[] }) {
  const hasAnyone = rosters.some((r) => r.starters.length > 0 || r.bench.length > 0);
  if (!hasAnyone) return null;

  return (
    <section className="card p-4">
      <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Lineups & player tracking</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {rosters.map((r) => <SideColumn key={r.teamCode || r.teamName} r={r} />)}
      </div>
      <p className="text-[10px] text-dim mt-2">Goals/assists/cards reflect this match. Substitutions sync with the timeline.</p>
    </section>
  );
}

function SideColumn({ r }: { r: RosterSide }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-sm font-semibold">{r.teamName || r.teamCode}</div>
        {r.formation && <div className="text-[10px] text-dim font-mono">{r.formation}</div>}
      </div>
      {r.starters.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-wider text-dim mb-1">Starting XI</div>
          <ul className="space-y-0.5 mb-3">
            {r.starters.map((p) => <PlayerRow key={p.id ?? p.name} p={p} />)}
          </ul>
        </>
      )}
      {r.bench.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-wider text-dim mb-1">Bench</div>
          <ul className="space-y-0.5">
            {r.bench.map((p) => <PlayerRow key={p.id ?? p.name} p={p} bench />)}
          </ul>
        </>
      )}
    </div>
  );
}

function PlayerRow({ p, bench }: { p: PlayerLine; bench?: boolean }) {
  return (
    <li className="flex items-baseline gap-1.5 text-xs">
      <span className="font-mono text-dim w-6 shrink-0 text-right">{p.jersey ?? '—'}</span>
      <span className={`flex-1 min-w-0 truncate ${bench ? 'text-dim' : ''}`}>
        {p.subbedOut && <span className="text-red-400/70 mr-1" title="Subbed off">↓</span>}
        {p.subbedIn && <span className="text-pitch-400 mr-1" title="Subbed on">↑</span>}
        {p.name}
      </span>
      <PlayerBadges p={p} />
      <span className="font-mono text-[10px] text-dim w-7 shrink-0 text-right">{p.position ?? ''}</span>
    </li>
  );
}

function PlayerBadges({ p }: { p: PlayerLine }) {
  const items: { icon: string; n: number; cls?: string; title: string }[] = [];
  if (p.stats.goals > 0) items.push({ icon: '⚽', n: p.stats.goals, title: 'Goals' });
  if (p.stats.assists > 0) items.push({ icon: '🅰', n: p.stats.assists, cls: 'text-cyan-400', title: 'Assists' });
  if (p.stats.yellowCards > 0) items.push({ icon: '🟨', n: p.stats.yellowCards, title: 'Yellow' });
  if (p.stats.redCards > 0) items.push({ icon: '🟥', n: p.stats.redCards, title: 'Red' });
  if (p.stats.saves > 0) items.push({ icon: '🧤', n: p.stats.saves, cls: 'text-dim', title: 'Saves' });
  if (items.length === 0) return null;
  return (
    <span className="flex items-center gap-1 shrink-0">
      {items.map((it, i) => (
        <span key={i} title={it.title} className={`text-[10px] ${it.cls ?? ''}`}>
          {it.icon}{it.n > 1 ? <span className="font-mono">×{it.n}</span> : ''}
        </span>
      ))}
    </span>
  );
}
